import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import FaqSection from '../components/Faq';
import { useTheme } from '../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'Is Base64 the same as encryption?', a: 'No. Base64 is a reversible encoding that anyone can decode. It provides no confidentiality and must never be used to protect passwords, keys or personal data.' },
  { q: 'Why is Base64 used in data URLs?', a: 'Data URLs embed files directly inside HTML or CSS. Base64 turns binary image or font data into text that fits in an attribute or stylesheet, removing a separate network request.' },
  { q: 'How much larger is Base64 data?', a: 'Base64 expands data by about 33%, because every 3 bytes become 4 characters. For large files this overhead can be significant.' },
  { q: 'What characters does Base64 use?', a: 'Standard Base64 uses A–Z, a–z, 0–9, "+" and "/", with "=" as padding. The URL-safe variant swaps "+" and "/" for "-" and "_".' },
];

export default function GuidePage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="What Is Base64? A Complete Guide to Base64 Encoding | EasyBase64"
        description="Learn what Base64 encoding is, how it works, when to use it, and how it differs from encryption and URL-safe Base64 — with practical examples and best practices."
        path="/what-is-base64"
        jsonLd={[
          articleSchema({
            headline: 'What Is Base64? A Complete Guide to Base64 Encoding',
            description: 'A complete guide to Base64 encoding: how it works, common use cases, URL-safe Base64, and why it is not encryption.',
            path: '/what-is-base64',
            datePublished: '2026-06-27',
            dateModified: '2026-06-27',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'What is Base64', path: '/what-is-base64' },
          ]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
      <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
        <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
          What Is Base64? A Complete Guide to Base64 Encoding
        </h1>

        <p className={para}>
          Base64 is one of the most widely used encoding schemes on the web, yet it is also one of
          the most misunderstood. This guide explains what Base64 is, how it works under the hood,
          where you will encounter it, and the difference between standard and URL-safe Base64. When
          you are ready to try it, jump straight to the <Link to="/" className="underline">Base64 converter</Link>.
        </p>

        <h2 className={heading}>What is Base64 encoding?</h2>
        <p className={para}>
          Base64 is a binary-to-text encoding that represents binary data using a set of 64 printable
          ASCII characters. The name comes from this radix-64 alphabet: the uppercase letters A–Z,
          the lowercase letters a–z, the digits 0–9, and the two symbols <code>+</code> and <code>/</code>.
          The <code>=</code> character is used for padding. Because every character is
          printable and safe, Base64 lets you move binary data through channels that were designed for
          plain text.
        </p>

        <h2 className={heading}>How does Base64 work?</h2>
        <p className={para}>
          Base64 takes your input three bytes (24 bits) at a time and splits those 24 bits into four
          groups of six bits. Each 6-bit group is a number from 0 to 63, which maps to one character
          in the Base64 alphabet. When the input length is not a multiple of three, the encoder pads
          the final group with one or two <code>=</code> characters so the output length is always a
          multiple of four. For example, the text <code>Hi</code> encodes to <code>SGk=</code>.
        </p>

        <h2 className={heading}>Common use cases for Base64</h2>
        <p className={para}>
          You will find Base64 almost everywhere data needs to travel as text: embedding images and
          fonts directly in HTML or CSS using data URLs, attaching files to email through MIME,
          carrying binary payloads inside JSON APIs, encoding the segments of a JSON Web Token (JWT),
          and storing small binary blobs in text-only databases or configuration files.
        </p>

        <h2 className={heading}>Standard vs. URL-safe Base64</h2>
        <p className={para}>
          The standard alphabet uses <code>+</code> and <code>/</code>, which have special meaning
          inside URLs. The <Link to="/url-safe-base64" className="underline">URL-safe variant</Link> replaces
          them with <code>-</code> and <code>_</code> and drops the <code>=</code> padding,
          so the encoded value can sit inside a query string, cookie or filename without further
          escaping. JWTs and OAuth tokens use this URL-safe form.
        </p>

        <h2 className={heading}>Base64 is not encryption</h2>
        <p className={para}>
          This is the single most important thing to remember: Base64 provides no security. It is a
          reversible encoding, so anyone can decode a Base64 string back to its original contents in
          seconds. Never use Base64 to hide passwords, API keys or personal data — for that you need
          real encryption. If you need to read a Base64 value, use the <Link to="/base64-decode" className="underline">Base64 decoder</Link>.
        </p>

        <h2 className={heading}>Encode and decode Base64 now</h2>
        <p className={para}>
          Ready to try it yourself? Use the <Link to="/base64-encode" className="underline">encoder</Link> to
          turn text into Base64, the <Link to="/base64-decode" className="underline">decoder</Link> to
          read a Base64 string, or the <Link to="/url-safe-base64" className="underline">URL-safe tool</Link> for
          tokens and URLs. Everything runs locally in your browser, so your data stays
          private.
        </p>
      </article>

      <FaqSection title="Base64 frequently asked questions" faqs={FAQS} />
      </div>
    </>
  );
}
